import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Cross-cutting registry of in-progress file uploads (DEV-485).
 *
 * Every uploader in the app funnels through `useDataPackage`, which reports its in-flight
 * uploads here so a single app-level navigation guard (UploadNavigationGuard) can warn the user
 * before they browse away and, on confirmation, cancel the not-yet-complete uploads.
 *
 * Active uploads are tracked as a `Set` of uploader ids keyed by the owning data package. Using a
 * set (rather than a +/- counter) makes start/end idempotent: ending an upload that was never
 * tracked (e.g. a reloaded failed row with no session uploaderId) or that already ended (a row
 * that errored then was cancelled) is a harmless no-op, so the "is anything uploading" signal can
 * never drift negative or get stuck.
 *
 * This is a module-level singleton (like LoggerService) so the many `useDataPackage` instances and
 * the single guard all share one source of truth without threading a React context through the app.
 */
class UploadActivityService {
    private readonly activeUploads = new Map<string, Set<string>>();
    private readonly cancelers = new Map<string, () => void>();
    private readonly activeSubject = new BehaviorSubject<boolean>(false);

    /** Emits whenever the "any upload in progress" state changes. */
    public readonly hasActiveUploads$: Observable<boolean> = this.activeSubject.asObservable();

    public hasActiveUploads = (): boolean => {
        for (const uploaderIds of this.activeUploads.values()) {
            if (uploaderIds.size > 0) {
                return true;
            }
        }
        return false;
    };

    /**
     * True while this specific uploader id is still transferring bytes for this data package.
     *
     * Callers use it to tell a finished file apart from one that is mid-upload — the descriptor's
     * own flags cannot: `isComplete` is false both before an upload starts and while it runs. A
     * descriptor rehydrated from the server has no `uploaderId` at all (it is client-only), so this
     * correctly reports false for files that are not part of this session's activity. (DEV-744)
     */
    public isUploadActive = (packageKey: string, uploaderId: string): boolean =>
        !!uploaderId && !!this.activeUploads.get(packageKey)?.has(uploaderId);

    public reportUploadStarted = (packageKey: string, uploaderId: string): void => {
        if (!uploaderId) {
            return;
        }
        let uploaderIds = this.activeUploads.get(packageKey);
        if (!uploaderIds) {
            uploaderIds = new Set<string>();
            this.activeUploads.set(packageKey, uploaderIds);
        }
        uploaderIds.add(uploaderId);
        this.emit();
    };

    public reportUploadEnded = (packageKey: string, uploaderId: string): void => {
        const uploaderIds = this.activeUploads.get(packageKey);
        if (uploaderIds) {
            uploaderIds.delete(uploaderId);
        }
        this.emit();
    };

    /** Registers a "cancel everything in flight" handler for a data package's uploader. */
    public registerCanceler = (packageKey: string, cancelAll: () => void): void => {
        this.cancelers.set(packageKey, cancelAll);
    };

    /** Drops all tracking for a data package (call on unmount). */
    public unregister = (packageKey: string): void => {
        this.cancelers.delete(packageKey);
        this.activeUploads.delete(packageKey);
        this.emit();
    };

    /** Aborts the not-yet-complete uploads of every registered uploader. Completed files are untouched. */
    public cancelAllActiveUploads = (): void => {
        this.cancelers.forEach((cancelAll) => {
            try {
                cancelAll();
            } catch {
                // best effort — one uploader failing to cancel should not block the others
            }
        });
    };

    private emit = (): void => {
        this.activeSubject.next(this.hasActiveUploads());
    };
}

export const uploadActivityService = new UploadActivityService();
